import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  selectedDate: string;
  onChange: (date: string) => void;
  days?: number;
  pillWidth?: number;
  pillHeight?: number;
}

function formatDate(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getDays(count: number): Date[] {
  const result: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    result.push(d);
  }
  return result;
}

// Short weekday labels (3-letter) — locale-independent abbreviations.
// We render localised forms via t() below; this only serves as a fallback.
const SHORT_WEEKDAY_FALLBACK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const DateCarousel: React.FC<Props> = ({ selectedDate, onChange, days = 14, pillWidth, pillHeight }) => {
  const { t } = useTranslation('customer');
  const containerRef = useRef<HTMLDivElement>(null);
  const dates = getDays(days);

  useEffect(() => {
    const selected = containerRef.current?.querySelector('[data-selected="true"]') as HTMLElement | null;
    if (selected) {
      selected.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedDate]);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        overflowX: 'auto',
        gap: 6,
        padding: '4px 16px 10px',
        scrollbarWidth: 'none',
      }}
    >
      {dates.map((date) => {
        const iso = formatDate(date);
        const isSelected = iso === selectedDate;
        const isToday = iso === formatDate(new Date());
        const weekday = t(`bookings.dateHeader.weekdays.${date.getDay()}`, {
          defaultValue: SHORT_WEEKDAY_FALLBACK[date.getDay()],
        });
        const month = t(`bookings.dateHeader.months.${date.getMonth()}`);
        return (
          <button
            key={iso}
            type="button"
            data-selected={isSelected ? 'true' : 'false'}
            onClick={() => onChange(iso)}
            style={{
              flexShrink: 0,
              width: pillWidth ?? 52,
              height: pillHeight ?? 64,
              borderRadius: 'var(--bl-radius)',
              border: isSelected
                ? '2px solid var(--bl-primary)'
                : '1px solid var(--bl-divider)',
              background: isSelected
                ? 'var(--bl-primary)'
                : 'var(--bl-surface)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              cursor: 'pointer',
              fontFamily: 'inherit',
              WebkitTapHighlightColor: 'transparent',
              padding: 0,
            }}
          >
            <span style={{
              fontSize: 11,
              fontWeight: 500,
              color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--bl-text-secondary)',
              lineHeight: 1,
            }}>
              {weekday}
            </span>
            <span style={{
              fontSize: 17,
              fontWeight: 700,
              color: isSelected ? '#fff' : 'var(--bl-text)',
              lineHeight: 1.1,
            }}>
              {date.getDate()}
            </span>
            <span style={{
              fontSize: 11,
              color: isSelected ? 'rgba(255,255,255,0.75)' : (isToday ? 'var(--bl-primary)' : 'var(--bl-text-secondary)'),
              fontWeight: isToday && !isSelected ? 600 : 400,
              lineHeight: 1,
            }}>
              {isToday && !isSelected ? t('bookings.dateHeader.today').toLowerCase() : month}
            </span>
          </button>
        );
      })}
    </div>
  );
};
